import React from 'react';

import en from '@/locales/en';
import es from '@/locales/es';
import pt from '@/locales/pt';
import { Locale } from '@/types/locale';

export const defaultLocale: Locale = 'en';

export type Translatable = { readonly [key in Locale]?: string } | Record<string, string>;

export type TranslationFn = (
  key: Translatable | string,
  ...args: ReadonlyArray<string | number>
) => string;

const identity: TranslationFn = (
  key: Translatable | string,
  ...args: ReadonlyArray<string | number>
): string => {
  if (!key || (typeof key === 'string' && key.trim() === '')) {
    return '';
  }

  const translatedString: string = typeof key === 'string' ? key : (key[defaultLocale] ?? '');
  if (!translatedString) {
    return String(key);
  }

  return executeTemplate(translatedString, ...args);
};

const createLocaleResolver =
  (language: Record<string, string>, locale: Locale): TranslationFn =>
  (key: Translatable | string, ...args: ReadonlyArray<string | number>): string => {
    if (typeof key === 'string') {
      const translatedString = language[key as keyof typeof language];

      if (!translatedString) {
        return identity(key, ...args);
      }

      return executeTemplate(translatedString, ...args);
    }

    try {
      const resolveTranslation = key[locale];

      if (!resolveTranslation) {
        return identity(key, ...args);
      }

      return executeTemplate(resolveTranslation, ...args);
    } catch (error) {
      console.debug(`[i18n] Translation error: ${error}`);
      return identity(key, ...args);
    }
  };

const portuguese: TranslationFn = createLocaleResolver(pt, 'pt');
const english: TranslationFn = createLocaleResolver(en, 'en');
const spanish: TranslationFn = createLocaleResolver(es, 'es');

const validateKey = (fn: TranslationFn): TranslationFn => {
  return (key: Translatable | string, ...args: ReadonlyArray<string | number>): string => {
    return fn(key, ...args);
  };
};

export const LocaleContext = React.createContext<Locale>('en');

export const useTranslator = (): TranslationFn => {
  const locale = React.useContext<Locale>(LocaleContext);
  if (!locale) {
    return identity;
  }

  // Check for exact matches first
  switch (locale) {
    case 'pt':
      return validateKey(portuguese);
    case 'en':
      return validateKey(english);
    case 'es':
      return validateKey(spanish);
    default:
      return identity;
  }
};

/**
 * Replaces placeholders in a template string with provided arguments.
 *
 * This function is designed to work with placeholder strings in the format `{index}`,
 * where "index" is the zero-based index of the argument to replace. Each placeholder
 * will be replaced by its corresponding value from the `args` array.
 *
 * @param template - The template string containing placeholders in the format `{index}`
 *                   (e.g., "Hello, {0}! Welcome to {1}.").
 * @param args - A readonly array of strings whose values will replace the corresponding
 *               placeholders in the `template`. Each placeholder `{index}` is replaced
 *               by the argument at the given `index`.
 * @returns - A new string where all placeholders in the `template` are replaced with
 *            their corresponding values from `args`.
 *
 * ## Example Usage
 * ```typescript
 * const greetingTemplate = "Hello, {0}! Welcome to {1}.";
 * const result = executeTemplate(greetingTemplate, "Alice", "Wonderland");
 * console.log(result); // Output: "Hello, Alice! Welcome to Wonderland."
 * ```
 *
 * ## Explanation of how templates work
 * - The `template` string contains placeholders in the form `{index}`, where `index` refers
 *   to the position of the argument in the `args` array.
 * - The function iterates over the `args` array using `reduce` and replaces every occurrence
 *   of `{0}`, `{1}`, `{2}`, etc., in the `template` string with the corresponding values from `args`.
 * - It uses a regular expression to find and replace all instances of a specific placeholder
 *   (e.g., `{0}`) globally in the `template`.
 * - The final string, after all replacements, is returned as the result.
 */
const executeTemplate = (template: string, ...args: ReadonlyArray<string | number>): string =>
  args.reduce((previous: string, current: string | number, index: number): string => {
    if (typeof current === 'string') {
      return previous.replace(new RegExp(`\\{${index}\\}`, 'g'), current);
    } else {
      return previous.replace(new RegExp(`\\{${index}\\}`, 'g'), current.toString());
    }
  }, template);
